import type { tr } from "./tr.js";

/**
 * English catalogue.
 *
 * ⚠️ Typed as `typeof tr` — a missing key **fails compilation**. The
 * "we forgot to translate that string" class of bug is impossible.
 *
 * ⚠️ English has **two** plural categories (`one`/`other`); Turkish has one.
 * The `_one` variants therefore exist only here, and `Intl.PluralRules`
 * selects the right suffix at runtime.
 *
 * ⚠️ **Binding promise language** (Rapor 08 §9): never write "end-to-end
 * encrypted", "100% accurate speaker" or "live captions".
 */
// ⚠️ `Record<keyof typeof tr, string>` TÜM tr anahtarlarını ZORUNLU kılar;
//    `& Record<string, string>` ise yalnız İngilizcede olan çoğul varyantlarına
//    (`_one`) izin verir. Eksik çeviri DERLENMEZ.
export const en: Record<keyof typeof tr, string> & Record<string, string> = {
  "app.name": "Centvox",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.retry": "Try again",
  "common.loading": "Loading…",
  "common.copy": "Copy",
  "locale.label": "Interface language",
  "a11y.skipToContent": "Skip to content",
  "common.copied": "Copied",

  "landing.title": "You need an invitation link to join a meeting",
  "landing.body":
    "Centvox works without an account. Open the link you were sent and you go straight into the meeting.",
  "landing.hostHint": "If you are the host, you can create a link from the admin panel.",

  // ── Marketing page (root path) ─────────────────────────────────────────
  // ⚠️ Promise language is binding (Report 08 §9). Forbidden here:
  //    "end-to-end encrypted", "100% accurate", "200+ people" (commitment: 50),
  //    "live captions" (text lags ~15 s).
  "landing.heroTagline": "Enterprise video meetings — no account, straight from the browser.",
  "landing.heroBody":
    "Participants open the link they were sent and join. No install, no extension, no sign-up.",
  "landing.capacity": "This environment supports up to {count} participants per meeting.",
  "landing.ctaJoin": "Join with my invitation link",
  "landing.ctaHow": "How it works",
  "landing.ctaAdmin": "Admin sign-in",
  "landing.pasteTitle": "Paste your invitation link",
  "landing.pasteLabel": "Invitation link",
  "landing.pastePlaceholder": "https://…/j/…#s=…",
  "landing.pasteHint": "Paste the whole link — the part after “#” is required too.",
  "landing.pasteInvalid": "This text does not look like a Centvox invitation link.",
  "landing.pasteSubmit": "Go to the meeting",
  "landing.howTitle": "How it works",
  "landing.how1Title": "The host creates the meeting",
  "landing.how1Body":
    "A meeting is created from the admin panel and two links are issued: one for participants, one for the host.",
  "landing.how2Title": "The participant opens the link",
  "landing.how2Body":
    "No account, password or install is asked for. Camera and microphone are checked before entering.",
  "landing.how3Title": "The meeting starts",
  "landing.how3Body":
    "Video and audio stream from the browser; chat, screen sharing and raise-hand are on the same screen.",
  "landing.featTitle": "Capabilities",
  "landing.feat1Title": "Lobby and PIN",
  "landing.feat1Body": "The host decides who gets in. A six-digit PIN can be added optionally.",
  "landing.feat2Title": "Screen sharing",
  "landing.feat2Body":
    "Share a window or tab on desktop browsers. iOS browsers do not allow screen sharing and the interface says so plainly.",
  "landing.feat3Title": "Chat and files",
  "landing.feat3Body":
    "In-meeting chat, mentions and file attachments. Attachments are scanned after upload and cannot be opened until the scan finishes.",
  "landing.feat4Title": "Background and noise",
  "landing.feat4Body":
    "Background blur and noise suppression. If the device cannot keep up the effect is downgraded automatically; the video does not freeze.",
  "landing.feat5Title": "Streaming transcript",
  "landing.feat5Body":
    "Speech can optionally be turned into text. The text appears with roughly 15 seconds of delay and its coverage ratio is reported.",
  "landing.feat6Title": "Attendance and export",
  "landing.feat6Body":
    "Who stayed how long downloads as CSV or JSON; transcript and summary come as separate files.",
  "landing.secTitle": "Security and privacy",
  "landing.sec1": "Meeting traffic is encrypted in transit with DTLS-SRTP.",
  "landing.sec2":
    "The invitation secret travels after the “#” in the address; browsers never send that part to the server.",
  "landing.sec3":
    "When the transcript is on, consent is asked first; the audio of anyone who declines is not processed.",
  "landing.sec4":
    "Attendance reports contain no IP addresses, and who downloaded a report is written to the audit log.",
  "landing.footerNote": "Centvox — enterprise video meetings without accounts.",
  "landing.footerNav": "Footer links",
  "landing.footerAdmin": "Admin panel",
  "landing.footerUsage": "Usage and cost",

  // ── Admin console ──────────────────────────────────────────────────────
  "admin.title": "Admin panel",
  "admin.subtitle":
    "Create and manage meetings. Access is verified through your organisation's identity provider via Cloudflare Access.",
  "admin.checking": "Checking access…",
  "admin.ready": "Your admin access is verified.",
  "admin.blockApiOrigin":
    "The request never reached the backend: this page called its own origin and got HTML instead of JSON. The bundle must be rebuilt with the correct API root (VITE_API_ORIGIN). This is not a CORS problem.",
  "admin.blockNetwork":
    "The server could not be reached. The usual cause is a CORS setting of “*”: requests carrying credentials cannot be sent with “*”. Add this page's address to CORS_ORIGINS on the Worker.",
  "admin.blockTenant":
    "No organisation record was found for this address. A row carrying the API's hostname must be added to the organizations table.",
  "admin.blockSso":
    "No session found. A Cloudflare Access application must be defined for this hostname, and access_issuer and access_aud must be set on the organisation record.",
  "admin.blockDomain": "Your account's email domain is not allowed for this organisation.",
  "admin.blockForbidden": "You are not authorised for this action.",
  "admin.blockQuota": "The monthly usage quota is exhausted; no new meeting can be started.",
  "admin.howLogin": "How do I sign in?",
  "admin.howLoginBody":
    "Centvox has no admin password. When this page opens, Cloudflare Access redirects you to your organisation's identity provider; after verification the panel opens by itself. Not showing a sign-in form is deliberate — it avoids opening a second, weaker identity surface.",
  "admin.createTitle": "New meeting",
  "admin.fieldTitle": "Meeting title",
  "admin.fieldStart": "Scheduled start (optional)",
  "admin.fieldMax": "Maximum participants",
  "admin.fieldTranscript": "Transcript / AI",
  "admin.transcriptOff": "Off",
  "admin.transcriptOnDemand": "On demand (host starts it)",
  "admin.transcriptAuto": "Automatic (starts with the meeting)",
  "admin.transcriptHint":
    "Any option other than Off asks participants for consent; those who decline are not transcribed.",
  "admin.create": "Create meeting",
  "admin.creating": "Creating…",
  "admin.createdTitle": "Meeting created",
  "admin.createdWarning":
    "These links are shown only once. Copy them before closing the page; the server cannot give them back.",
  "admin.guestLink": "Participant link",
  "admin.hostLink": "Host link",
  "admin.listTitle": "Meetings",
  "admin.listEmpty": "No meetings created yet.",
  "admin.purge": "Purge permanently",
  "admin.purgeQueued": "Purge queued",
  "admin.purgeFailed": "Could not submit the purge request.",
  "admin.purgeConfirmTitle": "Purge this meeting permanently?",
  "admin.purgeConfirmBody":
    "The record, audio, transcript and files of “{title}” will be permanently deleted. This cannot be undone.",
  "admin.purgeNote":
    "The purge runs in a queue and may take a few minutes. Meetings under legal hold cannot be purged.",
  "admin.colTitle": "Title",
  "admin.colStatus": "Status",
  "admin.colStart": "Start",
  "admin.usageLink": "Open the usage and estimated cost panel",

  // ── Role separation (migration 0007) ───────────────────────────────────
  "admin.roleOrgAdmin": "Organisation admin",
  "admin.roleMember": "Meeting organiser",
  "admin.devBypass": "Local development session",
  "admin.memberScopeHint": "You are seeing only the meetings you created.",
  "admin.myStatusTitle": "Your status",
  "admin.myEmail": "Account",
  "admin.myRole": "Role",
  "admin.myLiveNow": "Your meetings live now",
  "admin.myThisMonth": "Created this month",
  "admin.myTotal": "Your meetings in total",
  "admin.myScopeNote":
    "These numbers cover only the meetings you created; organisation-wide figures are visible to admins.",
  "admin.adminScopeHint":
    "As an organisation admin you are seeing every meeting in the organisation.",
  "admin.usersTitle": "Organisation users",
  "admin.ssoOperatorOnly":
    "SSO settings are managed by the operator. Contact your platform administrator to connect your identity provider — this is the root of trust for authentication and cannot be changed from the organisation panel.",
  // ── Host management (add / delete / reassign) ──────────────────────────
  "admin.addHost": "Add administrator",
  "admin.addHostEmail": "Email address",
  "admin.addHostHint": "Only addresses on the organisation's allowed domains can be added.",
  "admin.addHostAsAdmin": "Add as organisation administrator",
  "admin.hostAdded": "Administrator added and the access list was updated.",
  "admin.deleteHost": "Delete",
  "admin.deleteHostTitle": "Delete administrator",
  "admin.deleteHostBody": "{email} will be permanently deleted. This cannot be undone.",
  "admin.cannotDeleteSelf": "You cannot delete your own account.",
  "admin.reassignTo": "Reassign meetings to",
  "admin.reassignNone": "Do not reassign (no meetings)",
  "admin.reassignHint":
    "If this administrator owns meetings you must pick someone to take them over; meeting records are not deleted.",
  "admin.hostDeleted": "Administrator deleted.",
  "admin.hostDeletedReassigned": "Administrator deleted; {n} meetings reassigned.",
  "admin.accessSyncFailed":
    "Saved, but the Cloudflare Access list could not be updated; the user may be unable to sign in.",

  // ── SSO health ─────────────────────────────────────────────────────────
  "admin.ssoNotConfigured": "SSO is not configured; the temporary operator login is in use.",
  "admin.ssoHealthy": "SSO is active and the Access application is in place.",
  "admin.ssoUnknown":
    "SSO is active; the Access application state could not be verified right now.",
  "admin.ssoLockedOut":
    "SSO is active but the Access application is missing — the organisation cannot sign in. Repair required.",
  "admin.ssoRepairNeedsToken":
    "Automatic repair needs CF_ACCESS_TOKEN and CF_ACCESS_TEAM_DOMAIN to be set.",
  "admin.colEmail": "Email",
  "admin.colRole": "Role",
  "admin.colState": "Status",
  "admin.colActions": "Action",
  "admin.colTranscript": "Transcript",
  "admin.untitledMeeting": "(untitled meeting)",
  "invite.guestTitle": "Message to send participants",
  "invite.guestHint": "Copy this text as-is and send it to the participants.",
  "invite.hostTitle": "Message for the host",
  "invite.hostHint":
    "This link grants control of the meeting; send it only to whoever will run it.",
  "invite.copyMessage": "Copy message",
  "invite.whenLabel": "Date and time",
  "invite.linkLabel": "Join link",
  "invite.hostLinkLabel": "Host link",
  "invite.guestGreeting": "You are invited to \u201c{title}\u201d.",
  "invite.hostGreeting": "You will be running \u201c{title}\u201d.",
  "invite.guestHowTo":
    "Just open the link in your browser. No account, app or extension is needed. You can check your camera and microphone before joining.",
  "invite.hostHowTo":
    "Open the link in your browser. Starting the meeting, admitting people and ending it are only possible with this link.",
  "invite.hostWarning": "Do not share this link: anyone who opens it joins as host.",
  "invite.rawLinks": "Links only",
  "admin.statusScheduled": "Scheduled",
  "admin.statusLive": "In progress",
  "admin.statusEnded": "Ended",
  "admin.statusCancelled": "Cancelled",
  "admin.statusUnknown": "Unknown",
  "admin.viewTranscript": "Transcript",
  "admin.transcriptTitle": "Meeting transcript",
  "admin.transcriptLoading": "Loading transcript…",
  "admin.transcriptNone":
    "No transcript for this meeting. Transcription may never have been started.",
  "admin.transcriptPending": "The transcript is still being prepared. Check back in a few minutes.",
  "admin.transcriptFailed": "Could not load the transcript.",
  "admin.transcriptLines": "{count} lines",
  "admin.transcriptMore": "Showing the first part only. Download .md or .vtt for the full text.",
  "admin.colOutput": "Output",
  "admin.stateActive": "Active",
  "admin.stateDisabled": "Disabled",
  "admin.promote": "Make admin",
  "admin.demote": "Remove admin",
  "admin.disable": "Disable",
  "admin.enable": "Enable",
  "admin.you": "you",
  "admin.lastAdminWarning":
    "Only one active admin is left. The last admin cannot be demoted or disabled; promote someone else first.",

  // ── Admin panel: shell and sign-in ─────────────────────────────────────
  "admin.backHome": "Home",
  "admin.signOut": "Sign out",
  "admin.signOutFailed":
    "The local session was cleared, but the server-side token could NOT be revoked and may stay valid until it expires. Check your connection and sign out again; on a shared computer, close the browser completely.",
  "admin.sectionSso": "Authentication (SSO)",
  "operator.title": "Operator panel",
  "operator.loginTitle": "Operator sign-in",
  "operator.loginBody":
    "This panel manages the platform itself: creating organisations, wiring SSO and operator permissions. To manage your organisation's meetings, go to the admin panel.",
  "operator.loginCta": "Sign in with your corporate account",
  "operator.ssoNotConfigured":
    "Operator SSO is not configured. A separate Access application must be defined for the operator panel in Zero Trust, and OPERATOR_ACCESS_AUD must be set.",
  "operator.bootstrapTitle": "Create the first operator (break-glass)",
  "operator.bootstrapHint":
    "Only needed when no operator exists yet. This is not a sign-in: it creates the record, then you sign in with SSO. Every use is written to the audit log as its own entry.",
  "operator.bootstrapEmail": "Operator email",
  "operator.bootstrapSubmit": "Create the record",
  "operator.bootstrapDone":
    "Operator record is ready. You can now sign in with your corporate account.",
  "operator.bootstrapFailed": "Could not create the record. The key or the email is wrong.",
  "operator.tabOrgs": "Organisations",
  "operator.tabOperators": "Operators",
  "operator.orgsTitle": "Organisations",
  "operator.orgsEmpty": "No organisation has been created yet.",
  "operator.colOrg": "Organisation",
  "operator.colHostname": "Hostname",
  "operator.colPlan": "Plan",
  "operator.colSso": "SSO",
  "operator.ssoOn": "Connected",
  "operator.ssoOff": "Not connected",
  "operator.createTitle": "New organisation",
  "operator.createIntro":
    "An SFU application is created for the organisation before the record is written. Every organisation must have its own: track channels are global within an application, so the isolation boundary is the application.",
  "operator.fieldSlug": "Short name",
  "operator.fieldSlugHint":
    "Lowercase letters, digits and hyphens only. It goes into resource names and CANNOT be changed later.",
  "operator.fieldName": "Organisation name",
  "operator.fieldAdmin": "First administrator's email",
  "operator.fieldAdminHint":
    "This address is the organisation's bootstrap admin: on first sign-in it is created as an organisation administrator.",
  "operator.fieldHostname": "API hostname (optional)",
  "operator.fieldHostnameHint":
    "If given, tenant resolution binds to this address and SSO is provisioned automatically. If left empty the organisation runs on the shared address and SSO cannot be set up.",
  "operator.create": "Create organisation",
  "operator.creating": "Creating…",
  "operator.createdTitle": "Organisation created",
  "operator.pending": "Steps not finished yet:",
  "operator.operatorsTitle": "Operators",
  "operator.operatorsIntro":
    "Operators are responsible for the platform: they create organisations, wire SSO and manage each other. They do not have access to organisations' meetings.",
  "operator.addEmail": "New operator email",
  "operator.addEmailHint":
    "This address is recognised once the operator can sign in through the operator Access application. Adding it alone does not grant sign-in.",
  "operator.add": "Add operator",
  "operator.added": "Operator added.",
  "operator.colEmail": "Email",
  "operator.colState": "State",
  "operator.colAction": "Action",
  "operator.stateActive": "Active",
  "operator.stateOff": "Disabled",
  "operator.disable": "Disable",
  "admin.sections": "Panel sections",
  "admin.tabStatus": "Status",
  "admin.tabMeetings": "Meetings",
  "admin.tabUsers": "Users",
  "admin.tabOrg": "Organisation",
  "admin.loginTitle": "Admin sign-in",
  "admin.loginEmail": "Your email address",
  "admin.loginKey": "Operator key",
  "admin.loginKeyHint": "Obtained from whoever set this up. It is not stored in the browser.",
  "admin.login": "Sign in",
  "admin.loggingIn": "Signing in…",
  "admin.loginFailed": "Email or key is incorrect.",
  "admin.loginOrg": "Organisation: {name}",
  "admin.loginDisabled":
    "Interim sign-in is disabled for this organisation: no operator key is set, or SSO is active.",
  "admin.interimNotice":
    "This is an interim sign-in method and is weaker than enterprise SSO: it uses a shared key and has no multi-factor step. Once a custom domain is in place and Cloudflare Access is configured, this path closes by itself.",
  "admin.ssoTitle": "Corporate session required",
  "admin.ssoLogin": "Sign in with your corporate account",
  "admin.ssoBody":
    "Cloudflare Access is active for this organisation. Once you sign in with your identity provider this page opens by itself.",

  // ── Admin panel: status ────────────────────────────────────────────────
  "admin.sectionStats": "Summary",
  "admin.sectionCapabilities": "Platform capabilities",
  "admin.sectionLive": "Live rooms",
  "admin.sectionUnmeasured": "Not measurable",
  "admin.sectionAudit": "Recent audit entries",
  "admin.sectionOrg": "Organisation settings",
  "admin.statRoomsLive": "live rooms",
  "admin.statParticipants": "participants now",
  "admin.statRoomsStale": "stale records",
  "admin.statMeetings": "meetings total",
  "admin.statScheduled": "scheduled",
  "admin.statHosts": "users",
  "admin.statAdmins": "organisation admins",
  "admin.statDisabled": "disabled accounts",
  "admin.healthReady": "ready",
  "admin.healthUnavailable": "off",
  "admin.healthUnknown": "not measured",
  "admin.generatedAt": "Last updated {time}.",
  "admin.refresh": "Refresh",
  "admin.liveEmpty": "No live rooms right now.",
  "admin.liveStreaming": "live",
  "admin.liveStalled": "disconnected",
  "admin.colMeeting": "Meeting",
  "admin.colParticipants": "Participants",
  "admin.colStarted": "Started",
  "admin.colHeartbeat": "Heartbeat",
  "admin.freshRoom": "{sec} s ago",
  "admin.staleRoom": "{sec} s — stale, the room may have closed",
  "admin.auditNotice":
    "The audit log holds no personal data; identities are pseudonymous. Opening this list is itself recorded.",
  "admin.auditEmpty": "No audit entries yet.",

  // ── Admin panel: organisation settings ─────────────────────────────────
  "admin.save": "Save",
  "admin.saved": "Saved.",
  "admin.saveFailed": "Could not save.",
  "admin.transcriptE2ee": "E2EE on — transcript unavailable",
  "admin.viewSummary": "Summary",
  "admin.summaryTitle": "Meeting summary",
  "admin.summaryLoading": "Loading summary…",
  "admin.summaryNone":
    "No summary was produced for this meeting. Transcription may never have been started.",
  "admin.summaryPending": "The summary is still being prepared. Check back in a few minutes.",
  "admin.summaryFailed": "Could not load the summary.",
  "admin.summaryCoverage": "Transcript coverage: {pct}%",
  "admin.summaryDecisions": "Decisions",
  "admin.summaryActions": "Action items",
  "admin.summaryModel": "Model: {model}",
  "admin.downloadTranscriptMd": "Transcript (.md)",
  "admin.downloadTranscriptVtt": "Transcript (.vtt)",
  "admin.downloadSummaryMd": "Summary (.md)",
  "admin.fieldDomains": "Allowed email domains",
  "admin.fieldDomainsHint": "Comma separated. Example: company.com, company.co.uk",
  "admin.fieldAdminEmails": "Bootstrap admins",
  "admin.fieldAdminEmailsHint":
    "Comma separated. This list applies only on a person's FIRST sign-in; existing users' roles are changed on the Users tab.",
  "admin.fieldOperatorKey": "Operator key",
  "admin.fieldOperatorKeyHint":
    "This change is verified with the operator key, not your session — that is the way back if you lock yourself out.",
  "admin.ssoEnable": "Enable SSO",
  "join.title": "Join the meeting",
  "join.nameLabel": "Your display name",
  "join.namePlaceholder": "Your name",
  "join.pinLabel": "Meeting PIN",
  "join.submit": "Join",
  "join.submitting": "Joining…",
  "join.lobbyNotice": "This meeting has a waiting room; the host must let you in.",
  "join.invalidLink": "This join link is not valid. Please open the invitation link again.",
  "join.missingSecret": "The join link is incomplete. Use the full invitation link.",
  "join.notFound": "Meeting not found.",
  "join.failed": "Could not join the meeting.",
  "join.ended": "This meeting has ended.",
  "join.full": "The meeting is full.",
  "join.inviteExhausted": "This invitation link is invalid or already used.",
  "join.pinWrong": "Incorrect PIN.",
  "join.turnstileUnconfigured":
    "Bot protection is not configured in this environment, so joining cannot be accepted. Tell your administrator.",
  "join.turnstileUnreachable":
    "The verification service could not be reached. An ad blocker or network filter may be blocking challenges.cloudflare.com.",
  "join.turnstileFailed": "Verification could not be completed. Please try again.",
  "join.encryptionNotice": "Your call is encrypted in transit with DTLS-SRTP.",

  "green.title": "Check your devices",
  "green.camera": "Camera",
  "green.microphone": "Microphone",
  "green.speaker": "Speaker",
  "green.noCamera": "No camera found",
  "green.noMicrophone": "No microphone found",
  "green.permissionNeeded": "Camera and microphone permission is required.",
  "green.permissionDenied":
    "Permission was denied. You can allow camera and microphone access in your browser settings.",
  "green.deviceBusy":
    "The camera or microphone is in use by another application. Close it and try again.",
  "green.deviceUnsupported":
    "The browser cannot reach the camera or microphone. Make sure the page is served over HTTPS.",
  "green.deviceFailed": "The device could not be opened. Join with camera off or try again.",
  "green.joinMuted": "Join muted",
  "green.joinWithCameraOff": "Join with camera off",
  "green.micLevel": "Microphone level",
  "green.speakNow": "Say something — the bar should move.",

  "lobby.waiting": "Waiting for the host to let you in…",
  "lobby.cancel": "Stop waiting",
  "lobby.denied": "Your request to join was declined.",
  "lobby.knockTitle": "Waiting to join",
  "lobby.admit": "Admit",
  "lobby.deny": "Decline",
  "lobby.admitAll": "Admit everyone",
  "lobby.empty_one": "{count} person waiting",
  "lobby.empty_other": "{count} people waiting",

  "room.participants": "Participants",
  "room.chat": "Chat",
  "room.controls": "Meeting controls",
  "room.micOn": "Turn on microphone",
  "room.forcedMuteMic": "A moderator turned off your microphone.",
  "room.forcedMuteCam": "A moderator turned off your camera.",
  "room.forcedMuteScreen": "A moderator stopped your screen share.",
  "room.screenDenied": "You do not have permission to share your screen.",
  "room.screenPublishFailed": "Screen share could not start; capture was stopped.",
  "room.reconnectStalled": "Still trying to reconnect. You do not need to reload the page.",
  "room.camPublishFailed": "Your camera could not be published; it was turned off.",
  "room.noPublishCap": "You do not have permission to publish.",
  "room.micOff": "Turn off microphone",
  "room.camOn": "Turn on camera",
  "room.camOff": "Turn off camera",
  "room.screenShare": "Share screen",
  "room.screenStop": "Stop sharing",
  "room.raiseHand": "Raise hand",
  "room.lowerHand": "Lower hand",
  "room.leave": "Leave",
  "room.leaveConfirm": "Are you sure you want to leave the meeting?",
  "room.muted": "muted",
  "room.recording": "Transcribing",
  "room.elapsed": "Meeting duration",
  "room.peopleCount": "{count} people",
  "room.linkGood": "Connection good",
  "room.linkWeak": "Connection weak",
  "room.linkLost": "No connection",
  "room.you": "you",
  "room.host": "host",
  "room.cohost": "co-host",
  "room.presenter": "presenter",
  "room.viewer": "viewer",
  "room.handRaised": "raised their hand",
  "room.remoteMediaStalled":
    "No audio or video is arriving from the other side. Refresh the page to rebuild the connections.",
  "room.reconnecting": "Reconnecting…",
  "room.ended": "This meeting has ended",
  "room.endedBody":
    "The host ended the meeting. This link no longer works; you will need a new meeting link.",
  "room.endedLeave": "Back to home",
  "room.endedRedirect": "You will be taken to the home page in {count} seconds.",
  "room.endMeeting": "End meeting",
  "room.endConfirm":
    "The meeting will end for everyone and {count} people in the room will be disconnected. This cannot be undone.",
  "room.locked": "The meeting is locked; no one else can join.",
  "room.isOver": "This meeting has ended. The link no longer works.",
  "room.unlocked": "The meeting is unlocked.",
  "room.screenUnsupported": "Screen sharing is not supported on this device.",
  "room.removed": "You were removed from the meeting.",
  "room.participants_one": "{count} participant",
  "room.participants_other": "{count} participants",

  "chat.placeholder": "Write a message…",
  "chat.noSendCap": "You do not have permission to send messages.",
  "chat.send": "Send",
  "chat.empty": "No messages yet.",
  "chat.edited": "edited",
  "chat.deleted": "This message was deleted.",
  "chat.edit": "Edit",
  "chat.delete": "Delete",
  "chat.loadOlder": "Older messages",
  "chat.disabled": "Chat is turned off for this meeting.",
  "chat.externalLink": "External link",
  "chat.mentionedYou": "You were mentioned",
  "chat.pin": "Pin",
  "chat.unpin": "Unpin",

  "chat.attach": "Attach a file",
  "chat.attachments": "Attachments",
  "chat.typing": "{names} is typing…",
  "chat.attachedImage": "Attached image",
  "chat.showImage": "Show image",
  "chat.download": "Download",
  "chat.scanning": "Scanning — it will open shortly.",
  "chat.attachBlocked": "This file was flagged as malicious.",
  "chat.attachFailed": "The file could not be uploaded.",
  "chat.attachTooLarge": "The file is too large.",
  "chat.attachTooMany": "You can send at most 5 attachments.",
  "chat.attachTypeNotAllowed": "This file type is not accepted.",
  "chat.attachEmpty": "The file is empty.",
  "room.screenTile": "screen share",
  "room.pinTile": "Pin {name}",
  "room.unpinTile": "Unpin {name}",
  "room.tools": "Tools",
  "view.title": "View",
  "view.gallery": "Grid",
  "view.speaker": "Speaker",
  "view.hideSelf": "Hide my own video",
  "view.hideCamOff": "Hide people with camera off",
  "device.title": "Devices",
  "device.mic": "Microphone",
  "device.camera": "Camera",
  "device.auto": "Automatic",
  "device.outputHint":
    "Speaker output is chosen in your operating system's sound settings; some browsers do not let a page change it.",
  "device.switchFailed": "Could not switch device; another app may be using it.",
  "room.resizePanel": "Resize panel",
  "react.thumbsUp": "Thumbs up",
  "react.clap": "Applause",
  "react.heart": "Heart",
  "react.laugh": "Laugh",
  "react.wow": "Wow",
  "react.party": "Celebrate",
  "room.more": "More tools",
  "room.pages": "Pages",
  "room.prevPage": "Previous page",
  "room.nextPage": "Next page",
  "room.pageOf": "{page} of {total}",
  "room.react": "React",

  "host.mute": "Mute",
  "host.stopCam": "Turn off camera",
  "host.stopScreen": "Stop screen share",
  "host.kick": "Remove from meeting",
  "host.kickConfirm": "Remove {name} from the meeting?",
  "host.makeCohost": "Make co-host",
  "host.transferHost": "Transfer host role",
  "host.transferConfirm": "Transfer the host role to {name}? This cannot be undone.",
  "host.roomSettings": "Room settings",
  "host.allowScreenAll": "Anyone can share their screen",
  "host.allowScreenHint":
    "When off, only the host, co-hosts and presenters can share. You can still grant permission per person. Turning it off does not stop a share already in progress.",
  "host.lock": "Lock meeting",
  "host.unlock": "Unlock meeting",
  "host.endForAll": "End for everyone",
  "host.endConfirm": "End the meeting for everyone?",
  "host.grantScreen": "Allow screen sharing",
  "host.revokeScreen": "Revoke screen sharing",

  "quality.good": "Connection is good",
  "quality.lowerVideo": "Your connection is weak — video quality was reduced.",
  "quality.audioOnly": "Your connection is weak — switching to audio only.",
  "quality.hdDowngraded": "Your connection can't sustain HD — switched to standard quality.",
  "device.switched": "Your device changed — switched to a backup device.",
  "device.micLost":
    "Your microphone was removed and no backup was found — you are not being heard.",
  "device.cameraLost": "Your camera was removed and no backup was found — you are not being seen.",

  "status.audioLocked": "This device needs permission to play sound.",
  "status.unlockAudio": "Enable sound",
  "status.backgroundNotice": "Video and audio pause when you switch away from the app.",
  "status.updateReady": "A new version is ready.",
  "status.updateNow": "Update now",
  "status.offline": "No internet connection.",

  "transcript.title": "Streaming transcript",
  "transcript.start": "Start transcript",
  "transcript.stop": "Stop transcript",
  "transcript.delayNotice": "Text appears with roughly a 15-second delay.",
  "transcript.consentTitle": "Transcript consent",
  "transcript.consentBody":
    "Your microphone will be converted to text. If you decline, your audio is not processed and your name never appears as a speaker in the transcript.",
  "transcript.consentLimit":
    "Sound picked up by other microphones in the same room cannot be fully prevented.",
  "transcript.consentGranted": "Your audio is being transcribed.",
  "transcript.withdraw": "Withdraw my consent",
  "transcript.regrant": "Give consent again",
  "transcript.consentWithdrawn": "Your audio is not being transcribed.",
  "transcript.accept": "I agree",
  "transcript.decline": "I decline",
  "transcript.coverage": "Coverage: {pct}%",
  "transcript.translateTo": "Translation",
  "transcript.translateOff": "Off (source language)",
  "transcript.translating": "Translating…",
  "transcript.translatePartial": "The transcript is long; only part of it was translated.",
  "transcript.translateFailed": "Could not get the translation.",
  "transcript.historyFailed": "Earlier speech could not be loaded; only new lines will appear.",
  "transcript.search": "Search transcript",
  "transcript.waiting": "Speech will appear here in a few seconds.",
  "transcript.inactive": "The transcript is off.",
  // ═══════════════ Assistant / moderation / translation ═══════════════
  "assistant.title": "Meeting assistant",
  "assistant.ask": "Ask",
  "assistant.thinking": "Preparing an answer…",
  "assistant.placeholder": "What was discussed in this meeting?",
  "assistant.scopeNotice":
    "Answers come only from this meeting's transcript; general questions are not answered.",
  "assistant.sources": "Sources",
  "assistant.noSources": "The model gave no sources; verify the answer against the transcript.",
  "assistant.noTranscript": "There is no transcript for this meeting yet.",
  "assistant.noPermission": "You do not have permission to view the transcript.",
  "assistant.failed": "Could not get an answer.",
  "assistant.quotaSpent":
    "The daily quota for assistive AI is used up. Transcript and summary keep working.",
  "chat.reportsTitle": "Reported messages",
  "chat.reportsEmpty": "No reports.",
  "chat.reportFlagged": "Model flagged it",
  "chat.reportClean": "Model found no issue",
  "chat.reportPending": "Being reviewed",
  "chat.reportUnknown": "Could not be assessed — review manually",
  "chat.report": "Report",
  "chat.reported": "Your report was sent to the moderators.",
  "chat.reportFailed": "Could not send the report.",

  "usage.title": "Usage and estimated cost",
  "usage.period": "Period",
  "usage.estimatedTotal": "Estimated total",
  "usage.breakdown": "Breakdown by metric",
  "usage.metric": "Metric",
  "usage.amount": "Amount",
  "usage.source": "Source",
  "usage.confidence": "Confidence",
  "usage.priceVersion": "Price version",
  "usage.quota": "Quota usage",
  "usage.hardCapOn": "Hard cap is on — no new meetings once the quota is used up.",
  "usage.hardCapOff": "Hard cap is OFF — going over quota may appear on your bill.",
  "usage.srcEstimate": "estimate",
  "usage.srcApi": "Cloudflare API",
  "usage.srcReconciled": "reconciled",

  "error.rejoinConflict":
    "You already have an active broadcast in this meeting. Refresh the page and rejoin.",
  "error.generic": "Something went wrong.",
  "error.network": "Could not reach the server.",
  "error.rateLimited": "Too many requests. Please wait a moment.",
  "error.capabilityDenied": "You do not have permission to do that.",
  "error.planLimit": "This feature is not included in your plan.",
  "error.sessionExpired": "Your session has expired. Please reload the page.",
  "error.panelCrashed": "This panel failed to load. Your call is unaffected.",
  "error.appUpdated":
    "The app was updated. Refresh the page to load this section; you will not leave the meeting.",
  "error.refreshPage": "Refresh page",
  "error.reload": "Reload panel",
  // ⚠️ Not the user's fault: client and server versions disagree. The action is
  //    "reload", not "try harder".
  "error.clientOutdated": "This page is out of date. Please reload it.",
  "error.staleView": "That person has already left the meeting.",

  // ── Screen-share profiles ──────────────────────────────────────────────
  "screen.profileTitle": "Screen share quality",
  "screen.profile.text": "Text & slides — sharpest",
  "screen.profile.motion": "Video & motion — smooth",
  "screen.profile.hd": "HD video — 1080p, 60 fps",
  "audio.musicMode": "Music mode (headphones required)",
  "effects.title": "Audio & video",
  "effects.background": "Background",
  "effects.blurOff": "Off",
  "effects.blurLight": "Light blur",
  "effects.blurStrong": "Strong blur",
  "effects.blurUnavailable":
    "This device cannot run background effects smoothly, so the option is hidden to keep your video fluid.",
  "effects.modePrecise": "Person edges are detected.",
  "effects.modeBasic": "Basic blur is in use; edges may be rough.",
  "effects.noise": "Noise suppression",
  "effects.noiseOff": "Off",
  "effects.noiseBrowser": "Standard",
  "effects.noiseEnhanced": "Enhanced",
  "effects.noiseOffWarning":
    "With suppression off, speaker audio can leak back into your microphone and cause echo.",
  "effects.noiseFallback":
    "Enhanced mode is not available in this deployment; standard suppression is in use.",
  "effects.blurLost":
    "Your device could not keep up: background blur is OFF. Your background is visible.",
  "effects.micReacquireFailed":
    "Could not reopen the microphone; the previous setting was kept. Check the device.",
  "effects.degraded": "The background effect was reduced because the device could not keep up.",
  "keys.title": "Keyboard shortcuts",
  "keys.toggleMic": "Toggle microphone",
  "keys.toggleCam": "Toggle camera",
  "keys.toggleChat": "Toggle chat",
  "keys.toggleParticipants": "Toggle participant list",
  "keys.raiseHand": "Raise or lower hand",
  "keys.leave": "Leave the meeting",
  "keys.help": "Show this help",
};
